'use client';

import { useState } from "react";
import NextLink from "next/link";

import { Roles } from "@/__generated__/graphql";
import { useSession } from "next-auth/react";

import {
    alpha,
    Box,
    Button,
    Collapse,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";

import {
    AdminPanelSettingsRounded,
    CloseRounded,
    EditRounded,
    Inventory2Rounded,
} from "@mui/icons-material";

type Props = {
    productId?: string;
};

export default function ProductAdminFloatingActions({
    productId,
}: Props) {
    const { data: session } = useSession();
    const theme = useTheme();

    const [open, setOpen] = useState(false);

    const isAdmin = session?.user?.me.roles.includes(Roles.Admin);

    if (!productId || !isAdmin) {
        return null;
    }

    return (
        <Box
            sx={{
                position: "fixed",
                right: {
                    xs: 0,
                },
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: theme.zIndex.drawer + 20,
                display: "flex",
                alignItems: "center",
                transition: '0.25s all',
                gap: open ? 1 : 0,
            }}
        >
            {/* Floating Toggle Button */}
            <Tooltip
                arrow
                placement="left"
                title={
                    open
                        ? "بستن پنل مدیریت"
                        : "باز کردن پنل مدیریت محصول"
                }
            >
                <IconButton
                    onClick={() => setOpen((prev) => !prev)}
                    sx={{
                        width: 48,
                        height: 48,
                        ...!open ? {
                            borderRadius: 0,
                            borderTopLeftRadius: 24,
                            borderBottomLeftRadius: 24,
                        } : {
                            borderRadius: 4
                        },
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",

                        background: alpha(
                            theme.palette.background.paper,
                            0.82
                        ),

                        border: `1px solid ${alpha(
                            theme.palette.divider,
                            0.12
                        )}`,

                        boxShadow: `
                            0 10px 30px rgba(0,0,0,0.12),
                            0 2px 10px rgba(0,0,0,0.08)
                        `,

                        transition: "all .25s ease",

                        "&:hover": {
                            transform: "scale(1.06)",
                            background: alpha(
                                theme.palette.background.paper,
                                0.95
                            ),
                        },
                    }}
                >
                    {open ? (
                        <CloseRounded />
                    ) : (
                        <AdminPanelSettingsRounded />
                    )}
                </IconButton>
            </Tooltip>

            {/* Expandable Panel */}
            <Collapse
                in={open}
                orientation="horizontal"
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: 280,
                        borderRadius: 0,
                        borderTopLeftRadius: 18,
                        borderBottomLeftRadius: 18,
                        overflow: "hidden",
                        backdropFilter: "blur(18px)",
                        WebkitBackdropFilter: "blur(18px)",

                        background: alpha(
                            theme.palette.background.paper,
                            0.82
                        ),

                        border: `1px solid ${alpha(
                            theme.palette.divider,
                            0.12
                        )}`,

                        boxShadow: `
                            0 10px 30px rgba(0,0,0,0.12),
                            0 2px 10px rgba(0,0,0,0.08)
                        `,
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            px: 2,
                            py: 1.4,

                            borderBottom: `1px solid ${alpha(
                                theme.palette.divider,
                                0.08
                            )}`,

                            background: alpha(
                                theme.palette.primary.main,
                                0.04
                            ),
                        }}
                    >
                        <Typography
                            fontWeight={800}
                            fontSize={14}
                        >
                            مدیریت محصول
                        </Typography>

                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                mt: 0.4,
                                display: "block",
                                lineHeight: 1.7,
                            }}
                        >
                            دسترسی سریع به ابزارهای مدیریت این محصول
                        </Typography>
                    </Box>

                    {/* Actions */}
                    <Stack
                        spacing={1}
                        sx={{
                            p: 1.2,
                        }}
                    >
                        <Button
                            component={NextLink}
                            href={`/admin/products/${productId}`}
                            fullWidth
                            disableElevation
                            startIcon={<EditRounded />}
                            variant="contained"
                            sx={{
                                justifyContent: "flex-start",
                                borderRadius: 3,
                                transition: "all .2s ease",
                            }}
                        >
                            ویرایش محصول
                        </Button>

                        <Button
                            component={NextLink}
                            href={`/admin/variants/${productId}`}
                            fullWidth
                            startIcon={<Inventory2Rounded />}
                            variant="outlined"
                            sx={{
                                justifyContent: "flex-start",
                                borderRadius: 3,
                                transition: "all .2s ease",
                            }}
                        >
                            مدیریت تنوع‌ها
                        </Button>
                    </Stack>
                </Paper>
            </Collapse>
        </Box>
    );
}